import React from 'react';
import { Dropdown } from 'freemium-ui';
import { get, upperFirst } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ConditionType,
  CustomTableColumnType,
  FilterOperationType,
  FormServFieldType,
  UserType,
} from '../../../shared/types';
import Icon from '../../../shared/components/Icon';
import {
  ALL_FIELDS,
  FREEMIUM_CHECKBOX_TYPES,
  FREEMIUM_DROPDOWN_TYPES,
  FREEMIUM_INPUT_DATE_TIME_TYPES,
  FREEMIUM_INPUT_DATE_TYPES,
  FREEMIUM_INPUT_NUMBER_TYPES,
  FREEMIUM_INPUT_TYPES,
  FREEMIUM_MULTISELECT_DROPDOWN_TYPES,
  FREEMIUM_TEXTAREA_TYPES,
  FieldBasedFilterOperations,
  FilterOperations,
} from '../../../shared/constants';
import { FormField } from '../../../shared/styles';
import FilterOperation from './FilterOperation';

type FilterConditionProps = {
  onChange: (value: ConditionType | undefined) => void;
  field: FormServFieldType;
  columnSetting: CustomTableColumnType;
  form: UseFormReturn;
  value: ConditionType;
  userInfo?: UserType;
};

const FilterCondition = (props: FilterConditionProps) => {
  const { t, i18n } = useTranslation();
  const { value, onChange, field, form, userInfo } = props;
  const {
    formState: { errors, submitCount },
  } = form;
  const { id, label, type, name } = field;

  // updater_id, creater_id are the only names that will be used in priority
  const filterOptions =
    FieldBasedFilterOperations[name] || FieldBasedFilterOperations[type] || [];
  const filterOperation: FilterOperationType | undefined = value?.operator
    ? FilterOperations[value.operator]
    : undefined;

  const translatedFilterOptions = filterOptions.map((option) => {
    return { ...option, value: `${t(option.value)}` };
  });

  const selectedItem =
    translatedFilterOptions.find((item: any) => value?.operator === item.id) ||
    null;

  return (
    <FormField key={id}>
      {[
        ...FREEMIUM_CHECKBOX_TYPES,
        ...FREEMIUM_INPUT_TYPES,
        ...FREEMIUM_INPUT_DATE_TYPES,
        ...FREEMIUM_TEXTAREA_TYPES,
        ...FREEMIUM_INPUT_DATE_TIME_TYPES,
        ...FREEMIUM_INPUT_NUMBER_TYPES,
        ...FREEMIUM_DROPDOWN_TYPES,
        ...FREEMIUM_MULTISELECT_DROPDOWN_TYPES,
      ].includes(type) && (
        <>
          {value && (
            <div className="action" onClick={() => onChange(undefined)}>
              <div className="clear-filter">
                <Icon glyph="clear" width={8} height={8} />
                <span>{t('clear')}</span>
              </div>
            </div>
          )}
          <Dropdown
            id={id}
            label={
              i18n.exists(
                `contacts.import_contact.default_fields.contact.${name}`,
              )
                ? `${t(
                    `contacts.import_contact.default_fields.contact.${name}`,
                  )}`
                : label
            }
            {...(!selectedItem && !field.field_options?.filterable
              ? {
                  defaultIsOpen: true,
                  onSelect: (val, { closeMenu }: any = {}) => {
                    if (closeMenu) closeMenu();
                  },
                  onOuterClick: ({ closeMenu }) => {
                    if (closeMenu) closeMenu();
                  },
                }
              : {})}
            name={name}
            isRequired={filterOperation?.needValue}
            placeholder={`${t('forms.placeholders.choose_a_condition')}`}
            itemToString={(v: FilterOperationType) => upperFirst(v.value)}
            defaultSelectedItem={selectedItem}
            emptyItemsText={`${t('no_results_found')}`}
            onChange={(option: FilterOperationType) => {
              if (!option) return;
              onChange({
                name,
                evaluate_on: 'CONTACT',
                operator: option.id,
                field_type:
                  ALL_FIELDS[
                    option?.overrideFieldType
                      ? option?.inputValueType || type
                      : type
                  ],
                value: '',
              });
            }}
            items={translatedFilterOptions}
          />
          <FilterOperation
            errors={get(errors, [field.name], {})}
            value={value}
            showError={submitCount > 0}
            field={field}
            onChange={onChange}
            userInfo={userInfo}
          />
        </>
      )}
    </FormField>
  );
};

export default FilterCondition;
